<!--
  A person, as their chosen mark.

  TWO THINGS COME FROM THE SERVER AND NEITHER IS AN IMAGE: an avatar id out of a fixed
  set of 24, and a colour derived from the account id. Both are stable everywhere, which
  is the point — the same person is the same disc in the friends list, in a room and in
  the chat, on every device, with no upload and no bytes on the wire.

  THE COLOUR IS A SLOT, NOT A HEX. The server's hex is mapped to its `--person-N`
  theme token before it is drawn (`themedTint()` in tint.ts), so a mark recolours
  live when the theme changes — the person keeps their slot, the theme keys the hue.
  Identity survives ("same person, same slot"), and so does the owner's ask that the
  chrome's marks wear the theme they sit in.

  `$lib/ui/Avatar.svelte` is the shell's version of this and hashes a NAME. It is right
  for a room member who has no account. This one is right for anybody who has one, and
  the difference matters: a name can change, and a person whose avatar changed because
  they renamed themselves is a person you have to learn twice.
-->
<script lang="ts">
  import { markFor } from './glyphs';
  import { themedTint } from './tint';
  import { AVATARS, type AvatarId } from './protocol';

  interface Props {
    avatar?: AvatarId | null;
    /** The account's colour, CSS hex, straight from the server. */
    color?: string;
    size?: number;
    /** Draws a ring in the account colour: "this one is you", or "this one is picked". */
    selected?: boolean;
    /** Dimmed, for a friend who is not online. */
    muted?: boolean;
    label?: string;
    /**
     * A rounded square instead of a disc. Anything standing in for a person in
     * the chrome is square now — a lone circle reads as a stray dot, a square
     * reads as a deliberate piece of the interface.
     */
    square?: boolean;
  }

  let {
    avatar = null,
    color = 'var(--accent)',
    size = 36,
    selected = false,
    muted = false,
    square = false,
    label
  }: Props = $props();

  const paths = $derived(markFor(avatar));
  let failed = $state(false);
  const portrait = $derived(avatar && AVATARS.includes(avatar) ? `/avatars/v2/${avatar}.webp` : null);
  $effect(() => { void avatar; failed = false; });
  /** The server hex routed through its theme token; see the header note. */
  const tint = $derived(themedTint(color));
  /** Stroke scales with the disc so a 96 px mark is not a hairline and a 24 px one is
   *  not a blob. Clamped, because neither end is linear. */
  const weight = $derived(Math.max(1.15, Math.min(2, 34 / size + 0.75)));
</script>

<span
  class="mark"
  class:square
  class:selected
  class:muted
  style:width="{size}px"
  style:height="{size}px"
  style:--tint={tint}
  role={label ? 'img' : 'presentation'}
  aria-label={label}
  aria-hidden={label ? undefined : 'true'}
>
  {#if portrait && !failed}
    <img src={portrait} width={size} height={size} alt="" loading="lazy" decoding="async" onerror={() => { failed = true; }} />
  {:else}
  <svg viewBox="0 0 24 24" width={Math.round(size * 0.62)} height={Math.round(size * 0.62)} fill="none">
    {#each paths as d (d)}
      <path {d} stroke="currentColor" stroke-width={weight} stroke-linecap="round" stroke-linejoin="round" />
    {/each}
  </svg>
  {/if}
</span>

<style>
  .mark { overflow: hidden; }
  .mark img { width:100%; height:100%; object-fit:cover; display:block; }
  .mark {
    display: grid;
    place-content: center;
    flex: none;
    border-radius: 50%;
  }
  .mark.square {
    border-radius: var(--radius-xs);
    /* The account colour, brought to a lightness that reads on this dark surface
       whatever hue the server picked. Mixing rather than using it raw is what stops a
       dark blue account from becoming an invisible disc. */
    color: color-mix(in oklab, var(--tint) 78%, white);
    background: color-mix(in oklab, var(--tint) 20%, var(--surface-2));
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--tint) 30%, transparent);
    transition:
      box-shadow var(--dur-fast) var(--ease),
      opacity var(--dur-fast) var(--ease);
  }

  .selected {
    box-shadow:
      inset 0 0 0 1px color-mix(in oklab, var(--tint) 55%, transparent),
      0 0 0 2px var(--bg),
      0 0 0 4px color-mix(in oklab, var(--tint) 70%, transparent);
  }

  .muted {
    opacity: 0.45;
    filter: saturate(0.5);
  }
</style>
